import { pixelTextWidth } from "../../ui/kit/pixel-text.ts";
import { say } from "../../words.ts";
import { isUnboundCode } from "./actions.ts";

/**
 * The player's keyboard layout as far as the page can know it (P1.9-T50b and P1.9-T174; ADR-061's amendments "Move
 * joins the key table", point 6, and "every key rebindable but Escape", point 8): the character each physical key
 * prints, from the player's "Keyboard layout" choice, Auto reading `navigator.keyboard.getLayoutMap()` where the browser
 * has it and the US layout otherwise. It names keys and never changes what a key does: every action is matched by its
 * physical key alone.
 */

/**
 * The character each physical key of the main block types on a US layout, by `KeyboardEvent.code`.
 */
const US_CHARACTERS: Readonly<Record<string, string>> = Object.freeze({
  ...Object.fromEntries(Array.from({ length: 26 }, (_, index) => [`Key${String.fromCharCode(65 + index)}`, String.fromCharCode(97 + index)])),
  ...Object.fromEntries(Array.from({ length: 10 }, (_, digit) => [`Digit${digit}`, String(digit)])),
  Backquote: "`",
  Minus: "-",
  Equal: "=",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Comma: ",",
  Period: ".",
  Slash: "/",
});

/**
 * Where AZERTY (French) prints another character than the US layout on the letter keys and the US punctuation row.
 */
const AZERTY: Readonly<Record<string, string>> = Object.freeze({ KeyQ: "a", KeyW: "z", KeyA: "q", KeyZ: "w", KeyM: ",", Semicolon: "m", Comma: ";", Period: ":", Slash: "!" });

/**
 * Where QWERTZ (German) prints another character than the US layout on the letter keys.
 */
const QWERTZ: Readonly<Record<string, string>> = Object.freeze({ KeyY: "z", KeyZ: "y" });

/**
 * Where Dvorak (US) prints another character than the US layout on the letter keys and the US punctuation row.
 */
const DVORAK: Readonly<Record<string, string>> = Object.freeze({
  KeyQ: "'",
  KeyW: ",",
  KeyE: ".",
  KeyR: "p",
  KeyT: "y",
  KeyY: "f",
  KeyU: "g",
  KeyI: "c",
  KeyO: "r",
  KeyP: "l",
  BracketLeft: "/",
  BracketRight: "=",
  KeyS: "o",
  KeyD: "e",
  KeyF: "u",
  KeyG: "i",
  KeyH: "d",
  KeyJ: "h",
  KeyK: "t",
  KeyL: "n",
  Semicolon: "s",
  Quote: "-",
  KeyZ: ";",
  KeyX: "q",
  KeyC: "j",
  KeyV: "k",
  KeyB: "x",
  KeyN: "b",
  Comma: "w",
  Period: "v",
  Slash: "z",
  Minus: "[",
  Equal: "]",
});

/**
 * What the page knows of a layout: the character a physical key types, and the physical key a character is typed on.
 */
export interface KeyLayout {
  /**
   * The character a physical key types.
   *
   * @param code The `KeyboardEvent.code` value.
   * @returns The character, in lower case, or null when the layout does not say.
   */
  character(code: string): string | null;
  /**
   * The physical key a character is typed on.
   *
   * @param key The character, or a key's name.
   * @returns The code, a named key's own name, or null when the layout does not say.
   */
  codeOf(key: string): string | null;
}

/**
 * A key's name as browsers write them, which is also its code where the key types nothing.
 */
const NAMED = /^[A-Z][A-Za-z0-9]{1,19}$/;

/**
 * A layout from the characters its physical keys type.
 *
 * @param characters Each code's character.
 * @returns The layout.
 */
function layoutOf(characters: ReadonlyMap<string, string>): KeyLayout {
  const codes = new Map<string, string>();
  for (const [code, character] of characters) {
    if (!codes.has(character)) {
      codes.set(character, code);
    }
  }
  return {
    character: (code) => characters.get(code) ?? null,
    codeOf: (key) => codes.get(key) ?? (NAMED.test(key) ? key : null),
  };
}

/**
 * The US layout.
 */
export const US_LAYOUT: KeyLayout = layoutOf(new Map(Object.entries(US_CHARACTERS)));

/**
 * The player's choices of layout: Auto, by the browser's map, or one of the presets.
 */
export const LAYOUT_CHOICES = ["auto", "qwerty", "azerty", "qwertz", "dvorak"] as const;
export type LayoutChoice = (typeof LAYOUT_CHOICES)[number];

/**
 * The presets: the US layout with each one's differences over it, letters, digits and the US punctuation row only.
 */
export const PRESET_LAYOUTS: Readonly<Record<Exclude<LayoutChoice, "auto">, KeyLayout>> = Object.freeze({
  qwerty: US_LAYOUT,
  azerty: layoutOf(new Map(Object.entries({ ...US_CHARACTERS, ...AZERTY }))),
  qwertz: layoutOf(new Map(Object.entries({ ...US_CHARACTERS, ...QWERTZ }))),
  dvorak: layoutOf(new Map(Object.entries({ ...US_CHARACTERS, ...DVORAK }))),
});

/**
 * Reads a kept choice of layout.
 *
 * @param raw The kept text, or null.
 * @returns The choice; Auto for anything that is not one.
 */
export function parseLayoutChoice(raw: string | null): LayoutChoice {
  return (LAYOUT_CHOICES as readonly string[]).includes(raw ?? "") ? (raw as LayoutChoice) : "auto";
}

/**
 * The words a key that types nothing, or types a space, is named by.
 */
const NAMES: Readonly<Record<string, string>> = Object.freeze({
  Space: "Space",
  Enter: "Enter",
  NumpadEnter: "Enter",
  Tab: "Tab",
  Backspace: "Backspace",
  ShiftLeft: "Shift",
  ShiftRight: "Shift",
  ControlLeft: "Ctrl",
  ControlRight: "Ctrl",
  AltLeft: "Alt",
  AltRight: "Alt",
  MetaLeft: "Meta",
  MetaRight: "Meta",
  OSLeft: "Meta",
  OSRight: "Meta",
  ArrowUp: "Up",
  ArrowDown: "Down",
  ArrowLeft: "Left",
  ArrowRight: "Right",
  Escape: "Esc",
  PageUp: "Page Up",
  PageDown: "Page Down",
  NumpadAdd: "Num +",
  NumpadSubtract: "Num -",
  NumpadMultiply: "Num *",
  NumpadDivide: "Num /",
  NumpadDecimal: "Num .",
  ...Object.fromEntries(Array.from({ length: 10 }, (_, digit) => [`Numpad${digit}`, `Num ${digit}`])),
});

/**
 * Whether the pixel face draws a label.
 *
 * @param label The label.
 * @returns True when every glyph is in the face.
 */
function drawable(label: string): boolean {
  try {
    pixelTextWidth(label);
    return true;
  } catch {
    return false;
  }
}

/**
 * The label of a physical key: an unbound action's word (`isUnboundCode`); a named key's word; otherwise the character the layout prints on it in upper case, when
 * the pixel face draws it; otherwise the US character; otherwise the code itself, a function key's name among them.
 *
 * @param code The `KeyboardEvent.code` value.
 * @param layout The layout; the US layout unless given.
 * @returns The label.
 * @example
 * codeLabel("KeyW", PRESET_LAYOUTS.azerty);
 * // "Z"
 */
export function codeLabel(code: string, layout: KeyLayout = US_LAYOUT): string {
  if (isUnboundCode(code)) {
    return say("controls.unbound");
  }
  const name = NAMES[code];
  if (name !== undefined) {
    return name;
  }
  const own = layout.character(code)?.toUpperCase();
  if (own !== undefined && drawable(own)) {
    return own;
  }
  return US_CHARACTERS[code]?.toUpperCase() ?? code;
}

/**
 * Where a browser's layout map comes from.
 */
export interface LayoutSource {
  getLayoutMap(): Promise<ReadonlyMap<string, string>>;
}

/**
 * The page's layout: the player's choice, Auto by the browser's map once it answers and the US layout until then and
 * where it never does, or a preset. Its listeners hear when the map arrives under Auto and when the choice changes.
 */
export class PageLayout implements KeyLayout {
  private mapped: KeyLayout = US_LAYOUT;
  private chosen: LayoutChoice = "auto";
  private readonly listeners = new Set<() => void>();

  /**
   * @param source The browser's layout map, null where the browser has none.
   */
  public constructor(source: LayoutSource | null) {
    if (source === null) {
      return;
    }
    source.getLayoutMap().then(
      (map) => this.take(map),
      () => undefined,
    );
  }

  /**
   * The choice in force.
   *
   * @returns The choice.
   */
  public get choice(): LayoutChoice {
    return this.chosen;
  }

  /**
   * Takes a choice of layout, telling the listeners when it changes.
   *
   * @param choice The choice.
   */
  public choose(choice: LayoutChoice): void {
    if (choice === this.chosen) {
      return;
    }
    this.chosen = choice;
    this.tell();
  }

  /**
   * The layout in force.
   *
   * @returns The browser's map under Auto, else the preset.
   */
  private get current(): KeyLayout {
    return this.layoutFor(this.chosen);
  }

  /**
   * The layout a choice would put in force, for Controls to label its caps by the choice still pending.
   *
   * @param choice The choice.
   * @returns The browser's map, the US layout where it has none, for Auto; else the preset.
   */
  public layoutFor(choice: LayoutChoice): KeyLayout {
    return choice === "auto" ? this.mapped : PRESET_LAYOUTS[choice];
  }

  /**
   * The character a physical key types, by the layout in force and else the US layout.
   *
   * @param code The `KeyboardEvent.code` value.
   * @returns The character, or null.
   */
  public character(code: string): string | null {
    return this.current.character(code) ?? US_LAYOUT.character(code);
  }

  /**
   * The physical key a character is typed on, by the layout in force and else the US layout.
   *
   * @param key The character, or a key's name.
   * @returns The code, or null.
   */
  public codeOf(key: string): string | null {
    return this.current.codeOf(key) ?? US_LAYOUT.codeOf(key);
  }

  /**
   * Listens for the layout changing.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Takes the browser's map: each code with a one-character value, in lower case.
   *
   * @param map The map.
   */
  private take(map: ReadonlyMap<string, string>): void {
    const characters = new Map<string, string>();
    for (const [code, character] of map) {
      if (typeof code === "string" && typeof character === "string" && [...character].length === 1) {
        characters.set(code, character.toLowerCase());
      }
    }
    this.mapped = layoutOf(characters);
    if (this.chosen === "auto") {
      this.tell();
    }
  }

  /**
   * Tells every listener.
   */
  private tell(): void {
    for (const listener of [...this.listeners]) {
      listener();
    }
  }
}

/**
 * The browser's layout map, where it has one.
 *
 * @returns The source, or null.
 */
function browserSource(): LayoutSource | null {
  const keyboard = (globalThis as { readonly navigator?: { readonly keyboard?: Partial<LayoutSource> } }).navigator?.keyboard;
  return keyboard !== undefined && typeof keyboard.getLayoutMap === "function" ? (keyboard as LayoutSource) : null;
}

/**
 * The page's one layout.
 */
export const pageLayout = new PageLayout(browserSource());
